import React, { useMemo, useContext, useState } from 'react';
import moment from 'moment';
import { Table, Loader, CheckedRenderer } from '@ubeya/shared-web/components';
import useUnbookReasons from '@ubeya/shared/hooks/account/useUnbookReasons';
import useEmployeeShiftUnbookings from '@ubeya/shared/hooks/account/useEmployeeShiftUnbookings';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { SORT_BY_DATE_ONLY } from '@ubeya/shared/utils/sorting';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useAdmins from '@ubeya/shared/hooks/account/useAdmins';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import useTableSort from '@ubeya/shared/hooks/useTableSort';
import NotesRenderer from 'timeSheet/components/CellRenderers/NotesRenderer';

const UnbookedTable = ({ employeeId }) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { dateFormat, timeFormat } = useContext(ConfigContext);
  const { mappedClientBranches } = useCRM();
  const { isLoading, unbookingShifts } = useEmployeeShiftUnbookings({ employeeId });
  const { mappedAdmins } = useAdmins();
  const { unbookReasonsMap } = useUnbookReasons();
  const { dateAndTime } = useFormattingMap();
  const [sort, setSort] = useState({ sortBy: 'date', sortDirection: 'desc' });

  const columns = useMemo(
    () => [
      {
        Header: t('date'),
        dataKey: 'date',
        sortable: true,
        sortFunc: SORT_BY_DATE_ONLY('dateSort')
      },
      { Header: t('startTime'), dataKey: 'startTime', sortable: false },
      { Header: t('endTime'), dataKey: 'endTime', sortable: false },
      { Header: t('adminNotes'), dataKey: 'notes', sortable: false },
      {
        Header: t('admin'),
        dataKey: 'adminId',
        formatter: (value) => (value ? `${mappedAdmins[value]?.firstName} ${mappedAdmins[value]?.lastName}` : ''),
        sortable: false
      },
      {
        Header: t('client'),
        dataKey: 'clientBranchId',
        formatter: (value) => mappedClientBranches[value]?.name,
        sortable: false
      },
      { Header: t('type'), dataKey: 'type', formatter: (value) => t(value), sortable: false },
      {
        Header: t('employeeNotes'),
        dataKey: 'employeeApplicationComment',
        Cell: NotesRenderer,
        columnData: { readOnly: true, key: 'employeeApplicationComment' },
        sortable: false
      },
      {
        Header: t('unbookedAt'),
        dataKey: 'createdAt',
        formatter: (value) => dateAndTime(value, true),
        sortable: false
      },
      {
        Header: t('unbookReasonColumn'),
        dataKey: 'unbookReasonId',
        formatter: (value) => unbookReasonsMap[value]?.label || '',
        sortable: false
      },
      {
        Header: t('noShow'),
        dataKey: 'noShow',
        Cell: CheckedRenderer,
        sortable: false
      }
    ],
    [dateAndTime, mappedAdmins, mappedClientBranches, t, unbookReasonsMap]
  );

  const unsortedData = useMemo(
    () =>
      (unbookingShifts || []).map(
        ({
          id,
          shift,
          notes,
          unbookReasonId,
          clientBranchId,
          projectId,
          branchId,
          metadata,
          createdAt,
          employeeApplicationComment
        }) => {
          const { date, startTime, endTime } = shift;

          return {
            id,
            date: date && moment(date, API_DATE_FORMAT).format(`${dateFormat}, ddd`),
            dateSort: date,
            startTime: startTime && moment(startTime).format(timeFormat),
            endTime: endTime && moment(endTime).format(timeFormat),
            notes,
            unbookReasonId,
            noShow: !!unbookReasonsMap[unbookReasonId]?.countAsNoShow,
            clientBranchId,
            projectId,
            branchId,
            type: metadata?.type,
            adminId: metadata?.adminId,
            createdAt,
            employeeApplicationComment
          };
        }
      ),
    [dateFormat, timeFormat, unbookingShifts, unbookReasonsMap]
  );

  const data = useTableSort({ data: unsortedData, columns, sortBy: sort.sortBy, sortDirection: sort.sortDirection });

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Table
      columns={columns}
      data={data}
      shadow={false}
      sortable
      sortBy={sort.sortBy}
      sortDirection={sort.sortDirection}
      setSort={setSort}
      onRowClick={({ rowData }) => {
        window
          .open(`${accountId}/scheduling/branch/${rowData.branchId}/project/${rowData.projectId}`, '_blank')
          .focus();
      }}
    />
  );
};

export default UnbookedTable;
